import {isLoaded, Loaded} from 'Core/models/Async';
import {createValidatableEmptyEmail, ValidatableEmail, ValidatedEmail} from 'Core/models/Email';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {
  createValidatableEmptyPhone,
  isEmptyPhoneNumber,
  PhoneNumber,
  ValidatablePhone,
  ValidatedPhoneNumber,
} from 'Core/models/PhoneNumber';
import {isValid} from 'Core/models/Validatable';
import {
  createValidatableEmptyString,
  ValidatableNonEmptyString,
  ValidatedNonEmptyString,
} from 'Core/models/ValidatableNonEmptyString';
import {isRight, Left} from 'fp-ts/lib/Either';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export type AccountType = 'Internal' | 'External';

export class UnvalidatedBasicInformation {
  public readonly key = 'UnvalidatedBasicInformation';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly email: ValidatableEmail,
    public readonly phoneNumber: ValidatablePhone,
    public readonly accountType: AccountType | undefined,
    public readonly isUserSupport: boolean,
    public readonly isDisabled: boolean
  ) {}
}

export class InvalidBasicInformation {
  public readonly key = 'InvalidBasicInformation';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString | undefined,
    public readonly email: ValidatableEmail,
    public readonly phoneNumber: ValidatablePhone | undefined,
    public readonly accountType: AccountType | undefined,
    public readonly isUserSupport: boolean,
    public readonly isDisabled: boolean,
  ) {}
}

export class ValidInternalBasicInformation {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    username: ValidatableNonEmptyString,
    email: ValidatableEmail,
    phoneNumber: ValidatablePhone,
    isUserSupport: boolean,
    isDisabled: boolean
  ) {
    return isNonEmptyString(firstName) &&
      isNonEmptyString(lastName) &&
      isValid(username) &&
      (isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber)) &&
      isValid(email)
      ? new ValidInternalBasicInformation (firstName, lastName, username, email, phoneNumber, isUserSupport, isDisabled) : new InvalidBasicInformation(firstName, lastName, username, email, phoneNumber, 'Internal', isUserSupport, isDisabled);
  }

  public readonly accountType: AccountType = 'Internal';
  public readonly key = 'ValidInternalBasicInformation';
  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly email: ValidatedEmail,
    public readonly phoneNumber: ValidatedPhoneNumber | Loaded<Left<PhoneNumber>>,
    public readonly isUserSupport: boolean,
    public readonly isDisabled: boolean
  ) {}
}

export class ValidExternalBasicInformation {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    email: ValidatableEmail,
    username: ValidatableNonEmptyString | undefined,
    phoneNumber: ValidatablePhone | undefined,
    isUserSupport: boolean,
    isDisabled: boolean
  ) {
    return isNonEmptyString(firstName) &&
      isNonEmptyString(lastName) &&
      isValid(email) &&
      (!username || isValid(username)) &&
      (!phoneNumber || isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber))
      // @ts-ignore
      ? new ValidExternalBasicInformation(firstName, lastName, email, username, phoneNumber, isUserSupport, isDisabled)
      : new InvalidBasicInformation(
          firstName,
          lastName,
          username || new EmptyString(),
          email,
          phoneNumber || new EmptyString(),
          'External',
          isUserSupport,
          isDisabled
        );
  }

  public readonly accountType: AccountType = 'External';
  public readonly key = 'ValidExternalBasicInformation';
  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly email: ValidatedEmail,
    public readonly username: ValidatedNonEmptyString | undefined,
    public readonly phoneNumber: ValidatedPhoneNumber | Loaded<Left<PhoneNumber>> | undefined,
    public readonly isUserSupport: boolean,
    public readonly isDisabled: boolean
  ) {}
}

const createValidBasicInformation = (
  firstName: StringUnion,
  lastName: StringUnion,
  username: ValidatableNonEmptyString | undefined,
  email: ValidatableEmail,
  phoneNumber: ValidatablePhone | undefined,
  accountType: AccountType | undefined,
  isUserSupport: boolean,
  isDisabled: boolean
) => {
  if (accountType && accountType === 'Internal' && username) {
    return isNonEmptyString(firstName) &&
      isNonEmptyString(lastName) &&
      isValid(username) &&
      (!phoneNumber || isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber)) &&
      isValid(email) &&
      accountType
      ? ValidInternalBasicInformation.create(
          firstName,
          lastName,
          username,
          email,
          phoneNumber || EmptyBasicInformation.phoneNumber,
          isUserSupport,
          isDisabled
        )
      : new InvalidBasicInformation(firstName, lastName, username, email, phoneNumber, accountType, isUserSupport, isDisabled);
      
  } else if (accountType === 'External') {
    return isNonEmptyString(firstName) && isNonEmptyString(lastName) && isValid(email) && accountType
      ? ValidExternalBasicInformation.create(firstName, lastName, email, username, phoneNumber, isUserSupport, isDisabled)
      : new InvalidBasicInformation(firstName, lastName, username, email, phoneNumber, accountType, isUserSupport, isDisabled);
  } else {
    return new InvalidBasicInformation(
      firstName,
      lastName,
      username || EmptyBasicInformation.username,
      email,
      phoneNumber || EmptyBasicInformation.phoneNumber,
      accountType,
      isUserSupport,
      isDisabled
    );
  }
};

export const EmptyBasicInformation = new UnvalidatedBasicInformation(
  new EmptyString(),
  new EmptyString(),
  createValidatableEmptyString(),
  createValidatableEmptyEmail(),
  createValidatableEmptyPhone(),
  'Internal',
  false,
  false
);

export const isExternalBasicInformation = (
  basicInformation: BasicInformation
): basicInformation is ValidExternalBasicInformation | InvalidBasicInformation | UnvalidatedBasicInformation =>
  basicInformation.accountType === 'External';

export const changeAccountType = (accountType: AccountType) => (basicInformation: BasicInformation) => {
  const {firstName, lastName, email, username, phoneNumber, isUserSupport, isDisabled} = basicInformation;

  return createValidBasicInformation(
    firstName,
    lastName,
    username && isLoaded(username) && isRight(username.Data) ? username : undefined,
    email,
    phoneNumber && isLoaded(phoneNumber) && isRight(phoneNumber.Data) ? phoneNumber : undefined,
    accountType,
    isUserSupport,
    isDisabled
  );
};

export const changeFirstName = (firstName: string) => (basicInformation: BasicInformation) => {
  const {lastName, email, phoneNumber, username, accountType, isUserSupport, isDisabled} = basicInformation;

  return createValidBasicInformation(
    NonEmptyString.create(firstName),
    lastName,
    username,
    email,
    phoneNumber,
    accountType,
    isUserSupport,
    isDisabled
  );
};

export const changeLastName = (lastName: string) => (basicInformation: BasicInformation) => {
  const {firstName, email, phoneNumber, username, accountType, isUserSupport, isDisabled} = basicInformation;

  return createValidBasicInformation(
    firstName,
    NonEmptyString.create(lastName),
    username,
    email,
    phoneNumber,
    accountType,
    isUserSupport,
    isDisabled
  );
};

export const changeUserName = (userName: ValidatableNonEmptyString) => (basicInformation: BasicInformation) => {
  const {firstName, lastName, email, phoneNumber, accountType, isUserSupport, isDisabled} = basicInformation;

  return createValidBasicInformation(firstName, lastName, userName, email, phoneNumber, accountType, isUserSupport, isDisabled);
};

export const changeEmail = (email: ValidatableEmail) => (basicInformation: BasicInformation) => {
  const {firstName, lastName, username, phoneNumber, accountType, isUserSupport, isDisabled} = basicInformation;

  return createValidBasicInformation(firstName, lastName, username, email, phoneNumber, accountType, isUserSupport, isDisabled);
};

export const changePhoneNumber = (phoneNumber: ValidatablePhone) => (basicInformation: BasicInformation) => {
  const {firstName, lastName, username, email, accountType, isUserSupport, isDisabled} = basicInformation;

  return createValidBasicInformation(firstName, lastName, username, email, phoneNumber, accountType, isUserSupport, isDisabled);
};
export const changeIsUserSupport = (isUserSupport: boolean) => (basicInformation: BasicInformation) => {
  const {firstName, lastName, username, email, accountType, phoneNumber, isDisabled} = basicInformation;

  return createValidBasicInformation(firstName, lastName, username, email, phoneNumber, accountType, isUserSupport, isDisabled);
};

export const changeIsUserDisable = (isDisabled: boolean) => (basicInformation: BasicInformation) => {
  const {firstName, lastName, username, email, accountType, phoneNumber, isUserSupport} = basicInformation;
  
  return createValidBasicInformation(firstName, lastName, username, email, phoneNumber, accountType, isUserSupport, isDisabled);
};

export type BasicInformation =
  | ValidInternalBasicInformation
  | ValidExternalBasicInformation
  | InvalidBasicInformation
  | UnvalidatedBasicInformation;

export type BasicInformationOptions<TReturn> = ToMatchOptions<BasicInformation, 'key', TReturn>;

export const isValidBasicInformation = (
  basicInformation: BasicInformation
): basicInformation is ValidInternalBasicInformation | ValidExternalBasicInformation =>
  basicInformation.key === 'ValidInternalBasicInformation' || basicInformation.key === 'ValidExternalBasicInformation';

export const matchBasicInformation = <TReturn>(
  basicInformation: BasicInformation,
  options: BasicInformationOptions<TReturn>
): TReturn => {
  switch (basicInformation.key) {
    case 'InvalidBasicInformation':
      return options.InvalidBasicInformation(basicInformation);
    case 'UnvalidatedBasicInformation':
      return options.UnvalidatedBasicInformation(basicInformation);
    case 'ValidInternalBasicInformation':
      return options.ValidInternalBasicInformation(basicInformation);
    case 'ValidExternalBasicInformation':
      return options.ValidExternalBasicInformation(basicInformation);
  }
};
